import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { CheckCircle2, ArrowRight, DollarSign, Shield, Award, Facebook } from 'lucide-react';
import * as LucideIcons from 'lucide-react';
import { services, financingOptions } from '../data/mock';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';

const Services = () => {
  const IconComponent = ({ name, ...props }) => {
    const Icon = LucideIcons[name.split('-').map((word, i) => 
      i === 0 ? word : word.charAt(0).toUpperCase() + word.slice(1)
    ).join('')] || LucideIcons.Home;
    return <Icon {...props} />;
  };

  return (
    <div className="min-h-screen">
      {/* Hero Section */}
      <section className="relative bg-gradient-to-r from-navy-dark to-navy text-white py-24 mt-16">
        <div className="absolute inset-0 opacity-10">
          <img 
            src="https://images.unsplash.com/photo-1600585154526-990dced4db0d?w=1920&q=80"
            alt="Background"
            className="w-full h-full object-cover"
          />
        </div>
        <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h1 className="text-5xl md:text-6xl font-bold mb-6">
            Our <span className="text-gold">Services</span>
          </h1>
          <p className="text-xl text-gray-300 max-w-3xl mx-auto">
            Professional window and door replacement solutions designed to enhance your home's 
            energy efficiency, security, and curb appeal
          </p>
        </div>
      </section>

      {/* Services Grid */}
      <section className="py-20 bg-gradient-to-b from-white to-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid md:grid-cols-2 gap-8">
            {services.map((service, index) => (
              <Card 
                key={service.id}
                className="group hover:shadow-2xl transition-all duration-300 border-2 hover:border-gold/50 transform hover:-translate-y-1"
              >
                <CardHeader>
                  <div className="flex items-start gap-4">
                    <div className="w-16 h-16 bg-gradient-to-br from-gold/20 to-gold/10 rounded-xl flex items-center justify-center flex-shrink-0 group-hover:scale-110 transition-transform duration-300">
                      <IconComponent name={service.icon} className="text-gold" size={32} />
                    </div>
                    <div className="flex-1">
                      <CardTitle className="text-2xl text-navy-dark group-hover:text-gold transition-colors">
                        {service.title}
                      </CardTitle>
                    </div>
                  </div>
                </CardHeader>
                <CardContent>
                  <p className="text-gray-600 leading-relaxed mb-6">
                    {service.description}
                  </p>
                  <div className="space-y-3 mb-6">
                    <h4 className="font-semibold text-navy-dark">Key Features:</h4>
                    {service.features.map((feature) => (
                      <div key={`${service.id}-${feature}`} className="flex items-start gap-3">
                        <CheckCircle2 size={20} className="text-gold flex-shrink-0 mt-0.5" />
                        <span className="text-gray-700">{feature}</span>
                      </div>
                    ))}
                  </div>
                  <Link to="/contact">
                    <Button className="w-full bg-navy-dark hover:bg-navy text-white group-hover:bg-gold group-hover:text-navy-dark transition-all duration-200">
                      Get Free Quote
                      <ArrowRight className="ml-2" size={18} />
                    </Button>
                  </Link>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Process Section */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-4xl md:text-5xl font-bold text-navy-dark mb-4">
              Our <span className="text-gold">Process</span>
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Simple, transparent, and designed around your convenience
            </p>
          </div>

          <div className="grid md:grid-cols-4 gap-8">
            {[
              { step: '01', title: 'Free Consultation', desc: 'Schedule your free in-home assessment at your convenience' },
              { step: '02', title: 'Custom Quote', desc: 'Receive transparent pricing with no hidden fees or pressure' },
              { step: '03', title: 'Professional Install', desc: 'Expert installation by our certified technicians' },
              { step: '04', title: 'Final Walkthrough', desc: 'Ensure complete satisfaction with thorough inspection' }
            ].map((item, index) => (
              <div key={item.step} className="relative">
                <div className="text-center">
                  <div className="w-20 h-20 bg-gradient-to-br from-gold to-gold-light rounded-full flex items-center justify-center text-3xl font-bold text-navy-dark mx-auto mb-4 shadow-lg">
                    {item.step}
                  </div>
                  <h3 className="text-xl font-bold text-navy-dark mb-2">{item.title}</h3>
                  <p className="text-gray-600">{item.desc}</p>
                </div>
                {index < 3 && (
                  <div className="hidden md:block absolute top-10 left-[60%] w-[80%] h-0.5 bg-gradient-to-r from-gold to-transparent"></div>
                )}
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Gallery Section */}
      <section className="py-20 bg-gradient-to-b from-gray-50 to-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-4xl md:text-5xl font-bold text-navy-dark mb-4">
              Our <span className="text-gold">Work</span>
            </h2>
            <p className="text-xl text-gray-600 mb-8">
              Quality installations across Colorado's Front Range
            </p>
          </div>

          <div className="max-w-4xl mx-auto">
            <Card className="border-2 border-gold/30 shadow-xl">
              <CardContent className="p-12 text-center">
                <div className="mb-8">
                  <div className="w-20 h-20 bg-gradient-to-br from-gold/20 to-gold/10 rounded-full flex items-center justify-center mx-auto mb-6">
                    <Award className="text-gold" size={40} />
                  </div>
                  <h3 className="text-3xl font-bold text-navy-dark mb-6">
                    Serving Homes Throughout the Front Range
                  </h3>
                  <p className="text-lg text-gray-700 leading-relaxed mb-8">
                    From the mountains to the plains, we've completed thousands of window and door installations across Colorado. 
                    No project is too big or too small for our expert team. Whether you need a single window replaced or a complete 
                    home transformation, we bring the same level of dedication and craftsmanship to every job.
                  </p>
                </div>

                <div className="border-t border-gray-200 pt-8">
                  <p className="text-gray-600 mb-6 text-lg">
                    See our latest completed projects and customer transformations
                  </p>
                  <a
                    href="https://www.facebook.com/profile.php?id=61586173917757"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-flex items-center gap-3 bg-gold text-navy-dark px-8 py-4 rounded-lg font-bold text-lg hover:bg-gold-light transform hover:scale-105 transition-all duration-200 shadow-lg hover:shadow-xl"
                  >
                    <Facebook size={24} />
                    <span>View Our Work on Facebook</span>
                  </a>
                </div>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>

      {/* Financing Section */}
      <section className="py-20 bg-navy-dark text-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-4xl md:text-5xl font-bold mb-4">
              Flexible <span className="text-gold">Financing</span>
            </h2>
            <p className="text-xl text-gray-300 max-w-3xl mx-auto">
              Making quality windows and doors affordable for every budget
            </p>
          </div>

          <div className="grid md:grid-cols-3 gap-8">
            {financingOptions.map((option) => (
              <Card key={option.id} className="bg-white/5 backdrop-blur-sm border-gold/30 hover:border-gold transition-all duration-300 hover:transform hover:-translate-y-2">
                <CardContent className="p-8 text-center">
                  <div className="w-16 h-16 bg-gold/20 rounded-full flex items-center justify-center mx-auto mb-6">
                    <DollarSign className="text-gold" size={32} />
                  </div>
                  <h3 className="text-2xl font-bold mb-3">{option.title}</h3>
                  <p className="text-gray-300 mb-4 text-lg">{option.description}</p>
                  <p className="text-sm text-gold">{option.terms}</p>
                </CardContent>
              </Card>
            ))}
          </div>

          <div className="text-center mt-12">
            <Link to="/contact">
              <Button className="bg-gold text-navy-dark hover:bg-gold-light text-lg px-10 py-6 rounded-lg font-bold shadow-xl hover:shadow-gold/50 transform hover:scale-105 transition-all duration-200">
                Learn More About Financing
                <ArrowRight className="ml-2" />
              </Button>
            </Link>
          </div>
        </div>
      </section>

      {/* Warranties Section */}
      <section className="py-20 bg-gradient-to-b from-white to-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-4xl md:text-5xl font-bold text-navy-dark mb-4">
              Industry-Leading <span className="text-gold">Warranties</span>
            </h2>
          </div>

          <div className="grid md:grid-cols-2 gap-8 max-w-4xl mx-auto">
            <Card className="border-2 border-navy-dark/20 hover:border-gold transition-all duration-300">
              <CardContent className="p-8 text-center">
                <Shield className="text-gold mx-auto mb-4" size={48} />
                <h3 className="text-2xl font-bold text-navy-dark mb-3">Product Warranty</h3>
                <p className="text-gray-600 leading-relaxed">
                  Lifetime manufacturer warranty on all windows and doors. Coverage includes 
                  materials, glass, and hardware defects.
                </p>
              </CardContent>
            </Card>

            <Card className="border-2 border-navy-dark/20 hover:border-gold transition-all duration-300">
              <CardContent className="p-8 text-center">
                <Award className="text-gold mx-auto mb-4" size={48} />
                <h3 className="text-2xl font-bold text-navy-dark mb-3">Installation Warranty</h3>
                <p className="text-gray-600 leading-relaxed">
                  10-year workmanship guarantee on all installations. We stand behind our 
                  expert craftsmanship with complete peace of mind.
                </p>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 bg-gradient-to-r from-gold to-gold-light">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-4xl md:text-5xl font-bold text-navy-dark mb-6">
            Ready to Get Started?
          </h2>
          <p className="text-xl text-navy-dark/80 mb-10">
            Contact us today for your free consultation and estimate
          </p>
          <Link to="/contact">
            <Button className="bg-navy-dark text-white hover:bg-navy text-lg px-10 py-6 rounded-lg font-bold shadow-xl transform hover:scale-105 transition-all duration-200">
              Schedule Free Consultation
              <ArrowRight className="ml-2" />
            </Button>
          </Link>
        </div>
      </section>
    </div>
  );
};

export default Services;
